/**
 * P213: one animal's name, parents and life events.
 *
 * Self-contained card for the herd animal detail sheet. Mounted by the
 * breeding lane's integration patch (they own HerdScreen this batch):
 *   import { AnimalLifeLog } from '../components/AnimalLifeLog';
 *   <AnimalLifeLog animalId={a.id} />
 *
 * Names: animal.rename (sim validates; failures surface as alerts - the
 * repo's no-fake-ack convention). Parents come from the existing
 * sim.breeding pedigree projection. Life events come from the sim's lifelog
 * projection; every row is a stored recorded event (message verbatim), so
 * nothing here invents story text.
 */
import { lifeLogLabel } from '../life-log-labels';
import { useState } from 'preact/hooks';
import { useStore } from '../store';
import { lifeLogOf, lifeLogFor } from '../story-projection';

export function AnimalLifeLog({ animalId }: { animalId: string }) {
  const { sim, bridge } = useStore();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const animal = sim.animals.find((a) => a.id === animalId);
  const log = lifeLogOf(sim);
  const rows = lifeLogFor(log, animalId);
  const parents = sim.breeding?.pedigree?.[animalId];
  const nameOf = (id: string | null): string | null => {
    if (id === null) return null;
    return lifeLogLabel(id,sim.animals);
  };
  const saveName = () => {
    const name = draft.trim();
    if (name.length === 0) { setEditing(false); return; }
    bridge.send('animal.rename', { animal: animalId, name }); // success silent; rejection arrives as an alert
    setEditing(false);
  };
  return (
    <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin:0 0 14px;padding:10px 12px">
      <div class="eyebrow">Life log</div>

      <div class="row" style="margin:0;border:none;padding:8px 0;min-height:44px">
        <span class="row-main">
          <div class="row-label">Name</div>
          {editing ? (
            <input
              value={draft}
              maxLength={40}
              aria-label="Animal name"
              style="font-size:14px;padding:6px 8px;width:100%"
              onInput={(e) => { setDraft((e.target as HTMLInputElement).value); }}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
            />
          ) : (
            <div class="row-value" style="font-size:13px">{animal?.name ?? 'Unnamed'}</div>
          )}
        </span>
        {editing ? (
          <button class="btn btn-quiet" style="padding:8px 12px;font-size:11px;min-height:44px" onClick={saveName}>Save</button>
        ) : (
          <button
            class="btn btn-quiet"
            style="padding:8px 12px;font-size:11px;min-height:44px"
            onClick={() => { setDraft(animal?.name ?? ''); setEditing(true); }}
          >
            {animal?.name !== undefined ? 'Rename' : 'Name'}
          </button>
        )}
      </div>

      {(parents?.sire !== null && parents?.sire !== undefined) || (parents?.dam !== null && parents?.dam !== undefined) ? (
        <div class="row" style="margin:0;border:none;padding:8px 0;min-height:44px">
          <span class="row-main">
            <div class="row-label">Parents</div>
            <div class="row-value" style="font-size:13px">
              {[nameOf(parents.dam ?? null), nameOf(parents.sire ?? null)].filter((n) => n !== null).join(' × ')}
            </div>
          </span>
        </div>
      ) : null}

      {rows.length === 0 && (
        <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;padding:4px 0 6px">
          No recorded events yet. Births, arrivals, illness, treatment, moves and sales are logged here as they happen.
        </div>
      )}
      {rows.slice().reverse().map(({ entry, event }) => (
        <div key={entry.id} style="display:flex;gap:8px;padding:4px 0;align-items:baseline">
          <span class="row-label" style="flex:none;width:52px">Day {event.day}</span>
          <span class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em">{lifeLogLabel(event.message,sim.animals)}</span>
        </div>
      ))}
    </div>
  );
}
